import type { GanttLink, GanttTask } from './types';

/**
 * Shape of the JSON emitted by the MPXJ sidecar (docker/mpxj) — see its README.
 */
export interface MppScheduleTask {
    id: string;
    name: string;
    start: string;
    finish: string;
    durationDays: number;
    percentComplete: number;
    parentId: string | null;
    summary: boolean;
    milestone: boolean;
    resourceNames: string;
}

export interface MppScheduleLink {
    source: string;
    target: string;
    type: string;
    lag?: number;
}

export interface MppSchedule {
    project: {
        name?: string | null;
        start?: string | null;
        finish?: string | null;
        taskCount?: number;
    };
    tasks: MppScheduleTask[];
    links: MppScheduleLink[];
}

function clamp01(value: number): number {
    if (Number.isNaN(value)) {
        return 0;
    }
    return Math.min(1, Math.max(0, value));
}

/**
 * Map the sidecar's normalized schedule onto the DHTMLX task/link contract the
 * read-only viewer renders. Mirrors `transformTasksToGantt`, but for parsed MPP
 * data instead of CRM ProjectTask models.
 */
export function mppToGantt(data: MppSchedule | null | undefined): { tasks: GanttTask[]; links: GanttLink[] } {
    if (!data || !Array.isArray(data.tasks)) {
        return { tasks: [], links: [] };
    }

    // Only type a summary as a DHTMLX 'project' when it actually has children in
    // the dataset — a childless 'project' bar has no bounds to roll up and renders
    // empty. Everything else is a plain 'task' (milestones excepted).
    const parentIds = new Set(data.tasks.map((task) => task.parentId).filter((id): id is string => Boolean(id)));

    const tasks: GanttTask[] = data.tasks.map((task) => ({
        id: task.id,
        text: task.name,
        start_date: task.start,
        duration: Math.max(1, Math.floor(task.durationDays) || 1),
        progress: clamp01((task.percentComplete ?? 0) / 100),
        type: task.milestone ? 'milestone' : task.summary && parentIds.has(task.id) ? 'project' : 'task',
        parent: task.parentId ?? undefined,
        finish: task.finish,
        resources: task.resourceNames || '',
        open: true,
    }));

    const links: GanttLink[] = (Array.isArray(data.links) ? data.links : []).map((link, index) => ({
        id: index + 1,
        source: link.source,
        target: link.target,
        type: link.type ?? '0',
    }));

    return { tasks, links };
}
